import Logo from '../../components/Logo';
import { NativeBackButton } from '../../components/ux/NativeBackButton';

export default function Terms() {
  return (
    <div className="min-h-screen bg-white dark:bg-neutral-900 py-16 px-4 text-type-900 dark:text-white">
      <div className="max-w-4xl mx-auto">
        <NativeBackButton className="mb-4" />
        <div className="mb-8">
          <Logo link="/" />
        </div>
        
        <h1 className="text-4xl font-bold mb-8">Terms and Conditions of Use</h1>
        
        <div className="prose prose-lg max-w-none">
          <p className="text-sm text-gray-600 mb-8">
            <strong>Effective Date:</strong> 6/21/2026
          </p>

          <p className="mb-6">
            These Terms and Conditions ("Terms") govern your access to and use of the Outclose web application, operated by GBH Holdings Inc. ("Company," "we," "our," "us"), a Delaware corporation. By accessing or using Outclose (the "Platform"), you agree to be bound by these Terms. If you do not agree, you may not use the Platform.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Eligibility</h2>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>You must be at least 18 years of age to use Outclose.</li>
            <li>By using the Platform, you represent and warrant that you are at least 18 years old and have the legal authority to enter into these Terms.</li>
            <li>We may terminate accounts of users we reasonably suspect to be under 18 without prior notice.</li>
            <li>Knowingly communicating with, soliciting, or targeting a minor on the Platform is a standalone violation of these Terms regardless of intent.</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Account Registration</h2>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
            <li>You agree to provide accurate, current, and complete information when registering.</li>
            <li>We reserve the right to suspend or terminate accounts without notice for any reason.</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Community Standards</h2>
          <p className="mb-4">
            Outclose is designed to foster safe, respectful, and inclusive LGBTQ+ connections. You agree not to:
          </p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>Harass, discriminate, or exploit other users.</li>
            <li>Post unlawful, obscene, defamatory, or fraudulent information.</li>
            <li>Use the Platform for commercial purposes without authorization.</li>
            <li>Engage in spamming, fraud, or unlawful activity.</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Child Safety and Prohibition of Child Sexual Abuse and Exploitation (CSAE)</h2>
          <p className="mb-4">
            The safety of children is our highest priority. Outclose has a strict zero-tolerance policy for any form of Child Sexual Abuse and Exploitation (CSAE). For purposes of this section, a <strong>child or minor</strong> is any person under the age of 18.
          </p>
          <p className="mb-4">
            The following conduct is absolutely prohibited on the Platform and will result in immediate, permanent account termination and reporting to law enforcement:
          </p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li><strong>Child Sexual Abuse Material (CSAM):</strong> Creating, uploading, storing, sharing, distributing, or soliciting any visual depiction of a minor in sexually explicit conduct, regardless of whether it is real, illustrated, computer-generated, or fictional (including anime or AI-generated imagery).</li>
            <li><strong>Child grooming:</strong> Using the Platform to build a relationship with a minor for the purpose of facilitating sexual contact, soliciting sexual imagery, or otherwise exploiting a child.</li>
            <li><strong>Sextortion:</strong> Threatening or blackmailing a minor using intimate, explicit, or private images.</li>
            <li><strong>Child sex trafficking:</strong> Advertising, soliciting, facilitating, or participating in the commercial sexual exploitation of a child.</li>
            <li><strong>Sexual content involving minors:</strong> Posting any image, video, text, or other content that sexualizes or endangers a child, including images where a minor's face is obscured or the minor appears in the background of otherwise explicit material.</li>
            <li><strong>Impersonation:</strong> Pretending to be a minor or misrepresenting your age as under 18 for any purpose.</li>
            <li><strong>Solicitation:</strong> Attempting to obtain sexual content from or arrange sexual contact with any person you know or reasonably suspect to be a minor.</li>
          </ul>
          <p className="mb-4">
            CSAE violations are outside any warning or strike system. Upon discovery, we will:
          </p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>Immediately and permanently terminate the offending account.</li>
            <li>Report confirmed or suspected CSAM to the <strong>National Center for Missing &amp; Exploited Children (NCMEC) CyberTipline</strong> as required by 18 U.S.C. § 2258A, and cooperate with relevant national and international law enforcement agencies.</li>
            <li>Preserve content and account data in accordance with applicable law to support law enforcement investigations.</li>
          </ul>
          <p className="mb-4">
            To report suspected CSAE or CSAM, contact us immediately at <strong>team@outclose.com</strong>. You can also use the in-app reporting feature available on any user profile or content. Reports to NCMEC can be made directly at <strong>www.cybertipline.org</strong>.
          </p>
          <p className="mb-6">
            We may use automated detection technology, including hash-matching against industry-standard CSAM databases, to proactively identify prohibited content. By using the Platform, you consent to such scanning of user-generated content for child safety purposes.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Intellectual Property</h2>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>All intellectual property on the Platform—including text, code, graphics, and trademarks—is owned by or licensed to GBH Holdings Inc.</li>
            <li>You may not copy, modify, or distribute Platform content without written consent.</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Events and Dinners</h2>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>Outclose facilitates curated gatherings, including group dinners, but is not the host of these events.</li>
            <li><strong>Restaurant Charges:</strong> All food, beverages, tips, and any other charges incurred at a restaurant or venue are the sole responsibility of the attending user. Outclose does not collect or pay these charges on your behalf.</li>
            <li><strong>No Liability:</strong> Outclose is not liable for any injury, loss, dispute, or damages (including financial, personal, or health-related) arising from participation in events or dinners arranged through the Platform.</li>
            <li><strong>User Responsibility:</strong> Users are solely responsible for their behavior, interactions, and any agreements with restaurants or other attendees.</li>
            <li><strong>Community Events &amp; Third-Party Listings:</strong> The Platform lets you browse community events, some of which are aggregated from or hosted by third parties. We do not organize, endorse, or guarantee the accuracy, availability, or safety of events we did not create, and details such as date, time, location, and price may change or be inaccurate. Confirm details with the organizer before attending.</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Connecting with Other Users (Kikis)</h2>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>The Platform offers features—such as "Kikis"—that let you post and respond to invitations to meet other users for events or activities, and to message users you connect with.</li>
            <li>These connections, conversations, and any resulting in-person meetings are solely between users. Outclose facilitates introductions but is not a party to, and is not responsible for, your interactions or meetings with other users.</li>
            <li><strong>Your Safety:</strong> You are responsible for your own safety when meeting people through the Platform. We recommend meeting in public, telling someone where you'll be, and trusting your instincts.</li>
            <li>Content you post or share (including Kiki posts, applications, and messages) must comply with our Community Standards and may be reviewed, scanned, and moderated, including for the child-safety violations described above.</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Payments and Subscriptions</h2>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>Certain Platform features may require fees or subscriptions. Pricing and terms are displayed at purchase.</li>
            <li>Payments are non-refundable except where required by law.</li>
            <li>We reserve the right to adjust fees and notify users of any changes.</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Privacy</h2>
          <p className="mb-6">
            Your use of the Platform is also governed by our Privacy Policy. We use reasonable safeguards but cannot guarantee absolute security of your data.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Disclaimers</h2>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>The Platform is provided "as is" and "as available" without warranties of any kind.</li>
            <li>We do not guarantee uninterrupted service or verify all user identities.</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Limitation of Liability</h2>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>To the fullest extent permitted by law, GBH Holdings Inc. shall not be liable for indirect, incidental, or consequential damages.</li>
            <li>Our total liability shall not exceed the amount you paid in the 12 months prior to the claim.</li>
            <li>Outclose disclaims all liability connected with venues, restaurants, third-party service providers, or disputes between users.</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Indemnification</h2>
          <p className="mb-6">
            You agree to indemnify and hold harmless GBH Holdings Inc., its affiliates, and employees from claims or damages arising from your use of the Platform or participation in events.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Termination</h2>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>We may suspend or terminate your account for violations of these Terms or conduct harmful to the community.</li>
            <li>Upon termination, your rights under these Terms cease immediately.</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Governing Law and Jurisdiction</h2>
          <p className="mb-6">
            These Terms are governed by the laws of the State of Delaware, United States. Any disputes shall be resolved in the courts of Delaware, unless otherwise required by law.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Modifications</h2>
          <p className="mb-6">
            We may revise these Terms at any time. Updates will be effective upon posting. Continued use of the Platform constitutes acceptance of revised Terms.
          </p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Contact Information</h2>
          <p className="mb-2">For questions, please contact:</p>
          <p className="mb-1">📧 team@outclose.com</p>
          <p className="mb-8">📍 GBH Holdings Inc.</p>
        </div>
      </div>
    </div>
  );
}

